// The A/B group a dashboard is being read for: which experiment, which arm.
//
// It lives in the URL (`?experiment=&arm=`) like every other filter in this
// console, so "the funnel for the experiment arm" is a link somebody can be
// sent rather than a view they have to be talked through rebuilding.
//
// BOTH halves or neither. An arm without an experiment names nothing, and a
// half-applied filter is worse than no filter: it puts an unfiltered number
// under a filtered heading. The parse below is what enforces that, so no page
// has to remember it.
//
// ARMS COME FROM THE REGISTRY RECORD, not a constant: every experiment carries
// its own `arms` (baseline FIRST) on `GET /api/admin/experimentation` and on
// the A/B readout, so a third arm reaches the filter the day it is declared.
// `DEFAULT_ARMS` is only the fallback for a page that has not loaded a record
// yet, and mirrors `recruiter.experiments.PRODUCT_ARMS`.

import type { AbPresetWindow } from "@recruiter/shared/types";

/** The arms every product experiment has TODAY, baseline first. Mirrors
 *  `recruiter.experiments.PRODUCT_ARMS`; a record's own `arms` wins. */
export const DEFAULT_ARMS: readonly string[] = ["control", "experiment"];

/** @deprecated read the registry record's `arms`; kept for the one caller
 *  that predates records being available. */
export const ARMS = DEFAULT_ARMS;

export type Arm = string;

export type ExperimentScope = {
  experiment: string;
  arm: Arm;
};

/** The baseline of a registry record: its FIRST arm. */
export function baselineOf(arms: readonly string[] | undefined): string {
  return arms?.[0] ?? DEFAULT_ARMS[0]!;
}

/** How an arm reads on screen. Short, because it sits inside a chip: the
 *  baseline is "(A)", the non-baseline arms "(B)", "(C)", ... in declaration
 *  order, with the arm's own name so a third arm is not just a letter. */
export function armLabel(arm: string, arms: readonly string[] = DEFAULT_ARMS): string {
  const index = arms.indexOf(arm);
  const letter = index >= 0 ? String.fromCharCode(65 + index) : "?";
  const name = arm.charAt(0).toUpperCase() + arm.slice(1).replace(/_/g, " ");
  return `${name} (${letter})`;
}

/** The two-arm labels, kept for the callers that still index by name. */
export const ARM_LABEL: Record<string, string> = {
  control: armLabel("control"),
  experiment: armLabel("experiment"),
};

/** An arm from a URL param, accepted only when the given arms declare it. */
export function parseArm(raw: unknown, arms: readonly string[] = DEFAULT_ARMS): Arm | undefined {
  return typeof raw === "string" && arms.includes(raw) ? raw : undefined;
}

/** A scope from two URL params, or undefined when either half is missing or
 *  unrecognised. Deliberately total: a stale link naming a retired experiment
 *  reads as "no filter" and the page says so, rather than 404ing a reader who
 *  only wanted the dashboard. `armsFor` looks the experiment's arms up in the
 *  registry when the page has one; without it the default two are accepted. */
export function parseScope(
  experiment: unknown,
  arm: unknown,
  armsFor?: (experiment: string) => readonly string[] | undefined,
): ExperimentScope | undefined {
  const name = typeof experiment === "string" && experiment.length > 0 ? experiment : undefined;
  if (!name) return undefined;
  const parsed = parseArm(arm, armsFor?.(name) ?? DEFAULT_ARMS);
  return parsed ? { experiment: name, arm: parsed } : undefined;
}

/** The A/B readout's PRESET cohort windows, mirroring
 *  `recruiter.ab_test_snapshot.WINDOW_DAYS`. Deliberately not `custom`, which a
 *  readout can REPORT but nobody can ask the preset route for: a custom range is
 *  computed on demand on `/ab-tests/range?from=&to=` and the page holds it as a
 *  `DateRange`, not as a fifth switcher value. */
export const AB_WINDOWS = ["7d", "30d", "90d", "all"] as const;

/** A window from a URL param, or undefined for "use the page's default".
 *  Unrecognised reads as absent rather than throwing: a stale link should open
 *  the page, not an error. */
export function parseAbWindow(raw: unknown): AbPresetWindow | undefined {
  return typeof raw === "string" && (AB_WINDOWS as readonly string[]).includes(raw)
    ? (raw as AbPresetWindow)
    : undefined;
}

/** An `armsFor` lookup over the experimentation snapshot, for `parseScope`.
 *
 *  Without it `parseScope` only accepts the two PRODUCT arms, so a link naming
 *  a section's `absent` arm or an injection's `sent` arm parsed as "no filter"
 *  and the board quietly showed everybody. With it every registry key's own arms
 *  are accepted, which is what makes the arm filter work past the two cohorts
 *  (plan §3). A snapshot that has not loaded yet returns undefined for every
 *  key, so the default two still apply and the link degrades to unfiltered
 *  rather than to an error.
 */
export function armsFromSnapshot(
  data: { experiments: readonly { key: string; arms: readonly string[] }[] } | undefined,
): (experiment: string) => readonly string[] | undefined {
  const byKey = new Map<string, readonly string[]>();
  for (const record of data?.experiments ?? []) {
    if (record.arms.length > 0) byKey.set(record.key, record.arms);
  }
  return (experiment: string) => byKey.get(experiment);
}
